/*
 * Find visibility of box sides in a group of boxes for all four compass directions.
 * Please see Unit tests for usage samples.
 * dir is one of 'W', 'N', 'E', 'S'.
 */

const W = 'W'
const N = 'N'
const E = 'E'
const S = 'S'

/** Find all boxes obscuring passed box in passed direction. */
function findObscuringBoxes (dir, box, boxes) {
  const dirPred = (dir === W || dir === N) ? (a, b) => a < b : (a, b) => a > b
  const [d1, d2] = (dir === W || dir === E) ? [N, S] : [W, E]

  return boxes
    .filter(b => dirPred(b[dir], box[dir]))
    .filter(b => b[d2] > box[d1] && b[d1] < box[d2])
}

/** Find the 2 element box span line vector [a b] orthogonal to passed direction. */
function findBoxEdge (dir, box) {
  return (dir === W || dir === E) ? [box[N], box[S]] : [box[W], box[E]]
}

/**
 * Subtracts an edge from another edge, yielding zero to two results
 * (essentially Allen's interval algebra).
 */
export function subtractEdge ([a1, a2], [b1, b2]) {
  if (b1 <= a1 && b2 >= a2) { // covered -> no results
    return []
  }
  if ((b2 <= a1) || (b1 >= a2)) { // no overlap
    return [[a1, a2]]
  }
  if (b1 <= a1 && b2 > a1) { // overlap at start
    return [[b2, a2]]
  }
  if (b1 < a2 && b2 >= a2) { // overlap at end
    return [[a1, b1]]
  }
  return [[a1, b1], [b2, a2]] // overlap central -> 2 results
}

export function subtractEdges (inEdges, subEdges) {
  return subEdges.reduce(
    (accEdges, subEdge) =>
      accEdges
        .map(e => subtractEdge(e, subEdge))
        .reduce((acc, es) => [...acc, ...es], []), // ES6 is missing out on mapcat, huh?!
    inEdges)
}

export function edgeLength ([a, b]) {
  return b - a
}

export function edgesLength (edges) {
  return edges.map(edgeLength).reduce((a, b) => a + b, 0)
}

/** Compute ratio of visible edge length and total edge length. */
export function findEdgeVisibility (edge, edges) {
  return edgesLength(subtractEdges([edge], edges)) / edgeLength(edge)
}

export function findDirVisibility (dir, box, boxes) {
  return findEdgeVisibility(
    findBoxEdge(dir, box),
    findObscuringBoxes(dir, box, boxes)
      .map(b => findBoxEdge(dir, b)))
}

export function findDirVisibilities (box, boxes) {
  return [W, N, E, S]
    .reduce(
      (acc, dir) => {
        acc['vis' + dir] = findDirVisibility(dir, box, boxes)
        return acc
      }, {})
}
